import {
    Component,
    input,
    OnChanges,
    OnInit,
    output,
    signal,
    model,
    SimpleChanges,
    ViewEncapsulation,
} from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Button } from '../button/button.component';
import { Icon } from '../icon/icon.component';

/**
 * Paginador de listas.
 *
 * Calcula o total de páginas a partir de {@link total} e {@link pageSize},
 * exibe uma janela de páginas ({@link rangePagesShow}) e emite {@link pageChanged}
 * a cada navegação. A página atual é exposta como `model` ({@link currentPage}).
 */
@Component({
    selector: 'ai-paginator',
    imports: [NgFor, NgIf, FormsModule, Button, Icon],
    templateUrl: './paginator.component.html',
    styleUrls: ['./paginator.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
})
export class Paginator implements OnInit, OnChanges {
    /** Quantidade total de itens (obrigatório). */
    readonly total = input.required<number>();

    /** Quantidade de itens por página (obrigatório). */
    readonly pageSize = input.required<number>();

    /** Número máximo de botões de página exibidos na janela. */
    readonly rangePagesShow = input<number>(4);

    /**
     * Opções de "itens por página" exibidas no seletor à esquerda.
     *
     * Quando vazio (padrão), o seletor **não** é renderizado — mantém a
     * aparência atual das listas que não oferecem essa escolha. Ao informar
     * valores (ex.: `[8, 16, 32]`), o paginador mostra o campo "Por página" e
     * emite {@link pageSizeChanged} quando o usuário troca a opção.
     */
    readonly pageSizeOptions = input<number[]>([]);

    /** Página atual (two-way via `model`). */
    readonly currentPage = model<number>(1);

    protected readonly totalPages = signal<number>(0);
    protected readonly pagesShow = signal<number[]>([]);

    /** Todas as páginas [1..totalPages] — alimenta o dropdown de página (mobile). */
    protected readonly allPages = signal<number[]>([]);

    protected firstItem = signal<number>(1);
    protected lastItem = signal<number>(0);

    /** Emite o número da nova página sempre que ela muda. */
    pageChanged = output<number>();

    /**
     * Emite o novo tamanho de página quando o usuário seleciona outra opção no
     * campo "Por página" (só disponível quando {@link pageSizeOptions} tem
     * valores).
     */
    pageSizeChanged = output<number>();

    ngOnInit(): void {
        this.calculateTotalPages();
        this.updateItemRange();
        this.updatePagesShow();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['total'] || changes['pageSize']) {
            this.calculateTotalPages();
            this.updateItemRange();
            this.updatePagesShow();
        }
    }

    protected calculateTotalPages(): void {
        const totalPages = Math.ceil(this.total() / this.pageSize());
        this.totalPages.set(totalPages);
        this.allPages.set(
            Array.from({ length: Math.max(totalPages, 0) }, (_, i) => i + 1),
        );
    }

    private updateItemRange(): void {
        if (this.total() === 0) {
            this.firstItem.set(0);
            this.lastItem.set(0);
            return;
        }
        this.firstItem.set((this.currentPage() - 1) * this.pageSize() + 1);
        this.lastItem.set(
            Math.min(this.currentPage() * this.pageSize(), this.total()),
        );
    }

    private updatePagesShow(): void {
        let startPage = Math.max(
            1,
            this.currentPage() - Math.floor(this.rangePagesShow() / 2),
        );
        let endPage = startPage + this.rangePagesShow() - 1;
        if (endPage > this.totalPages()) {
            endPage = this.totalPages();
            startPage = Math.max(1, endPage - this.rangePagesShow() + 1);
        }
        const pages = [];
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        this.pagesShow.set(pages);
    }

    protected onClickNext(): void {
        const nextPage = this.currentPage() + 1;
        this.onPageChange(nextPage);
    }

    protected onClickPrevious(): void {
        const previousPage = this.currentPage() - 1;
        this.onPageChange(previousPage);
    }

    protected onClickPage(page: number): void {
        this.onPageChange(page);
    }

    protected onClickFirst(): void {
        this.onPageChange(1);
    }

    protected onClickLast(): void {
        this.onPageChange(this.totalPages());
    }

    onPageChange(page: number): void {
        if (page < 1 || page > this.totalPages()) {
            return;
        }
        this.currentPage.set(page);
        this.updateItemRange();
        this.updatePagesShow();
        this.pageChanged.emit(this.currentPage());
    }

    /**
     * Trata a troca de "itens por página": apenas repassa o novo tamanho ao
     * container (via {@link pageSizeChanged}). O recálculo de páginas acontece
     * quando o `pageSize` chega de volta como input (após a nova busca).
     *
     * @param pageSize Novo tamanho de página selecionado.
     */
    onPageSizeChange(pageSize: number): void {
        this.pageSizeChanged.emit(pageSize);
    }
}
